import React, { useState, useEffect, useCallback } from 'react';

import { isArray } from 'lodash';

import { fetchGroupListService } from 'actions/group';
import AppBar from 'components/AppBar';
import ApplicationOverlayContent from 'components/ApplicationLayout/ApplicationOverlayContent';
import Button from 'components/Buttons/Button';
import ConfirmBottomSheet from 'components/ConfirmBottomSheet';
import Loader from 'components/Loader';
import ModalHeader from 'components/ModalHeader';
import PaymentCardItem from 'components/Payment/PaymentCardItem';
import SwitchItem from 'components/Payment/SwitchItem';
import { Desktop, Mobile } from 'components/Responsive';
import useIsMobile from 'hooks/useIsMobile';
import useStoreTranslation from 'hooks/useStoreTranslation';
import useToast from 'hooks/useToast';
import { Group } from 'models/group';
import { PaymentCard, PaymentGroup, PaymentCardData } from 'models/paymentCard';
import { getAllPaymentCards, removeGroupsCard, saveGroupsCard } from 'services/paymentMethodService';
import { htmlT } from 'utils/generic';
import { cardTypeNormalize } from 'utils/payment';

import PaymentHelp from '../PaymentHelp';
import ButtonHelp from '../PaymentHelp/components/ButtonHelp';
import { Content, Title, SubTitle, ButtonContainer } from './styles';

interface Props {
  selectedCard?: PaymentCard;
  defaultCard?: PaymentCard;
  onClose: () => void;
  onUpdate: () => void;
}

interface PaymentGroupCard {
  groupId: string;
  groupName: string;
  cardId: string;
  cardType: string;
  cardLastFourDigits: string;
  checked: boolean;
}

interface ConfirmData {
  show: boolean;
  title?: string;
  text?: string;
  onConfirm?: () => void;
}

const sortList = (a: PaymentGroupCard, b: PaymentGroupCard) =>
  a.groupName.toUpperCase() < b.groupName.toUpperCase()
    ? -1
    : a.groupName.toUpperCase() > b.groupName.toUpperCase()
    ? 1
    : 0;

const PaymentLinkGroup = ({ selectedCard, defaultCard, onClose, onUpdate }: Props) => {
  const toast = useToast();
  const { t } = useStoreTranslation();
  const isMobile = useIsMobile();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [disabled, setDisabled] = useState(true);
  const [list, setList] = useState<PaymentGroupCard[]>([]);
  const [changedList, setChangedList] = useState<PaymentGroupCard[]>([]);
  const [originList, setOriginList] = useState<PaymentGroupCard[]>([]);
  const [confirmData, setConfirmData] = useState<ConfirmData>({ show: false });
  const [showHelp, setShowHelp] = useState(false);

  const readData = useCallback(
    (groups, cards) => {
      let listGroups: PaymentGroupCard[] = [];
      if (groups && groups.length > 0) {
        listGroups = groups.map((g: Group) => {
          let card = defaultCard;
          if (cards && cards.length > 0) {
            const cc = cards.filter((c: PaymentCardData) => {
              if (c.linkedGroups && c.linkedGroups.length > 0) {
                const found = c.linkedGroups.find((p: PaymentGroup) => p.id === g.id);
                if (found) {
                  return true;
                }
              }
              return false;
            });
            card = cc && cc.length > 0 ? cc[0].card : defaultCard;
          }
          let cardType = card?.cardType;
          cardType = cardType ? cardTypeNormalize(cardType).toUpperCase() : '';
          const item = {
            groupId: g.id,
            groupName: g.name,
            cardId: card?.cardId,
            cardType: cardType,
            cardLastFourDigits: card?.last4Digits,
            checked: card?.cardId === selectedCard.cardId,
          };
          return item;
        });
      }
      listGroups = listGroups.sort(sortList);
      setList(listGroups);
      setOriginList(listGroups);
    },
    [defaultCard, selectedCard],
  );

  const handleRequests = useCallback(() => {
    setIsLoading(true);
    Promise.all([fetchGroupListService(), getAllPaymentCards()])
      .then((values) => {
        let groups = [];
        let cards;
        for (const i in values) {
          if ('config' in values[i]) {
            groups = values[i]['data']['data'];
          } else {
            if (isArray(values[i])) {
              cards = values[i];
            }
          }
          readData(groups, cards);
        }
      })
      .catch((error) => {
        toast.add({
          type: 'danger',
          message: error.message,
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [readData, toast]);

  useEffect(() => {
    handleRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = useCallback(async () => {
    setIsSaving(true);
    const finalList = checkList(changedList, originList);
    if (finalList.length > 0) {
      const groupsRemove = finalList.filter((p) => p.cardId === defaultCard?.cardId);
      const groupsChange = finalList.filter((p) => p.cardId !== defaultCard?.cardId);
      if (typeof selectedCard !== 'undefined' && typeof defaultCard !== 'undefined') {
        try {
          if (groupsRemove.length > 0 && groupsChange.length > 0) {
            await Promise.all([
              removeGroupsCard(
                selectedCard?.cardId,
                groupsRemove.map((p) => p.groupId),
              ),
              saveGroupsCard(
                selectedCard?.cardId,
                groupsChange.map((p) => p.groupId),
              ),
            ]);
            setIsSaving(false);
            onUpdate();
            return;
          }

          if (groupsRemove.length > 0) {
            await removeGroupsCard(
              selectedCard?.cardId,
              groupsRemove.map((p) => p.groupId),
            );
            setIsSaving(false);
            onUpdate();
            return;
          }

          await saveGroupsCard(
            selectedCard?.cardId,
            groupsChange.map((p) => p.groupId),
          );
          setIsSaving(false);
          onUpdate();
        } catch (error) {
          toast.add({
            type: 'danger',
            message: error.message,
          });
          setIsSaving(false);
          onUpdate();
        }
      }
    } else {
      setIsSaving(false);
      onClose();
    }
  }, [changedList, originList, onClose, onUpdate, toast, defaultCard, selectedCard]);

  const handleChange = (e, currentCard: PaymentGroupCard, defaultCard?: PaymentCard) => {
    const checked = e.target.checked;
    const savedCard = originList.find((o: PaymentGroupCard) => o.groupId === currentCard.groupId);

    if (savedCard && savedCard.checked === checked) {
      changeCardGroup(checked, currentCard, defaultCard);
      return;
    }
    if (currentCard.cardId === selectedCard?.cardId) {
      changeCardGroup(checked, currentCard, defaultCard);
      return;
    }

    if (currentCard.cardId === defaultCard?.cardId) {
      changeCardGroup(checked, currentCard, defaultCard);
      return;
    }

    if (currentCard.cardId !== defaultCard?.cardId && currentCard.cardId !== selectedCard?.cardId) {
      setConfirmData({
        show: true,
        title: `${htmlT(t, 'payment.paymentCardGroup.alertChangeCard.title', { group: currentCard.groupName })}`,
        text: `${htmlT(t, 'payment.paymentCardGroup.alertChangeCard.text', { group: currentCard.groupName })}`,
        onConfirm: () => {
          changeCardGroup(checked, currentCard, defaultCard);
        },
      });
    }
  };

  const changeCardGroup = (checked: boolean, currentCard: PaymentGroupCard, defaultCard?: PaymentCard) => {
    const savedCard = originList.find((o: PaymentGroupCard) => o.groupId === currentCard.groupId);

    let changedGroupCard = {
      ...currentCard,
      checked: checked,
    };

    if (savedCard) {
      if (savedCard.checked) {
        if (!checked) {
          if (defaultCard) {
            changedGroupCard = {
              ...currentCard,
              cardId: defaultCard?.cardId,
              cardLastFourDigits: defaultCard.last4Digits,
              cardType: cardTypeNormalize(defaultCard.cardType).toUpperCase(),
              checked: checked,
            };
          }
        } else {
          changedGroupCard = {
            ...currentCard,
            cardId: savedCard.cardId,
            cardLastFourDigits: savedCard.cardLastFourDigits,
            cardType: cardTypeNormalize(savedCard.cardType).toUpperCase(),
            checked: checked,
          };
        }
      } else {
        if (checked) {
          if (selectedCard) {
            changedGroupCard = {
              ...currentCard,
              cardId: selectedCard?.cardId,
              cardLastFourDigits: selectedCard.last4Digits,
              cardType: cardTypeNormalize(selectedCard.cardType).toUpperCase(),
              checked: checked,
            };
          }
        } else {
          changedGroupCard = {
            ...currentCard,
            cardId: savedCard.cardId,
            cardLastFourDigits: savedCard.cardLastFourDigits,
            cardType: cardTypeNormalize(savedCard.cardType).toUpperCase(),
            checked: checked,
          };
        }
      }
    }

    let cList = changedList.length > 0 ? changedList.filter((p) => p.groupId !== changedGroupCard.groupId) : [];
    cList = [...cList, changedGroupCard].sort(sortList);
    setChangedList(cList);
    let nList = list.length > 0 ? list.filter((p) => p.groupId !== changedGroupCard.groupId) : [];
    nList = [...list.filter((p) => p.groupId !== changedGroupCard.groupId), changedGroupCard].sort(sortList);
    setList(nList);

    const finalList = checkList(cList, originList);
    setDisabled(finalList.length === 0);
  };

  const checkList = (changedList: PaymentGroupCard[], originList: PaymentGroupCard[]): PaymentGroupCard[] => {
    const cList = changedList.filter((c: PaymentGroupCard) => {
      const r = originList.filter((o: PaymentGroupCard) => o.groupId === c.groupId && o.cardId !== c.cardId);
      if (r.length > 0) {
        return true;
      }
      return false;
    });

    return cList;
  };

  const renderHelp = () => (
    <ApplicationOverlayContent sidebar show={showHelp} onClose={() => setShowHelp(false)}>
      <PaymentHelp onClose={() => setShowHelp(false)} title={t('payment.paymentCardGroup.header')} />
    </ApplicationOverlayContent>
  );

  return (
    <>
      <Mobile>
        <AppBar goBack={onClose}>
          <h5>{t('payment.paymentCardGroup.header')}</h5>
        </AppBar>
      </Mobile>

      {isLoading ? (
        <Loader fullScreen={isMobile} />
      ) : (
        <>
          <Desktop>
            <ModalHeader title={t('payment.paymentCardGroup.header')} onClose={onClose} />
          </Desktop>

          {renderHelp()}
          <Content>
            <Title>
              {t('payment.paymentCardGroup.title')}
              <ButtonHelp onClick={() => setShowHelp(true)} />
            </Title>
            {typeof selectedCard !== 'undefined' ? <PaymentCardItem card={selectedCard} iconType="empty" /> : null}
            <SubTitle>{t('payment.paymentCardGroup.subtitle')}</SubTitle>
            {list
              ? list.map((p, idx) => (
                  <SwitchItem
                    key={idx}
                    title={p.groupName}
                    subtitle={`${p.cardType} *${p.cardLastFourDigits}`}
                    onToggle={(e) => handleChange(e, p, defaultCard)}
                    value={p.checked}
                  />
                ))
              : null}
            <ButtonContainer>
              <Button type="button" onClick={() => handleSave()} disabled={disabled || isSaving} loading={isSaving}>
                {t('payment.paymentCardGroup.btnSave')}
              </Button>
            </ButtonContainer>
            <ConfirmBottomSheet
              show={confirmData.show}
              title={confirmData.title ? confirmData.title : ''}
              onClose={() => setConfirmData({ show: false })}
              onConfirm={() => {
                if (confirmData.onConfirm) confirmData.onConfirm();
              }}
              content={confirmData.text ? confirmData.text : ''}
            />
          </Content>
        </>
      )}
    </>
  );
};

export default PaymentLinkGroup;
